import { ArrowDown } from 'lucide-react';
import type { ComponentType, ReactElement, ReactNode } from 'react';

import { cn } from '@/shared/lib/utils';
import { Highlight } from '@/shared/ui/highlight';

import { AutomationFlowArt, LessonStackArt } from './service-art';
import { SLIDE_FRAME, SlideEyebrow, SlideNumber } from './slide-parts';

type Service = {
  readonly id: string;
  readonly order: string;
  /** 제목 앞부분 — 뒤의 `highlight` 에 형광펜을 긋는다 */
  readonly lead: string;
  readonly highlight: string;
  readonly description: ReactNode;
  readonly chips: readonly string[];
  /** 같은 페이지 안의 다음 볼거리로 보낸다 — 문의 버튼은 히어로·끝에 이미 있다 */
  readonly more: { readonly label: string; readonly href: string };
  readonly Art: ComponentType;
};

/**
 * 서비스 안내 — 오톱이 하는 일 두 가지. 슬라이드 한 장에 서비스 하나.
 *
 * 문구의 근거는 이 사이트에 이미 있는 말이다 — FAQ(원리부터 설명, 코드 없음, 다루는 도구),
 * 자료실 사이드바(「자동화 컨설팅 및 교육 — 진단부터 실습, 정착까지」). 새 약속을 만들지 않는다.
 */
const SERVICES: readonly Service[] = [
  {
    id: 'edu-service-training',
    order: '01',
    lead: '우리 팀 업무에 맞춘 ',
    highlight: 'AI 교육',
    description: (
      <>
        LLM이 어떻게 답하는지 원리부터 짚고, 그 위에서{' '}
        <b className="font-semibold text-foreground">실제 업무에 붙이는 법</b>을 실습합니다. 코드를
        쓰지 않아 비전공자도 따라옵니다.
      </>
    ),
    chips: ['LLM·디퓨전 원리', 'ChatGPT', 'Claude', 'Gemini', 'Genspark', 'Grok'],
    more: { label: '교육 과정 보기', href: '#edu-process' },
    Art: LessonStackArt,
  },
  {
    id: 'edu-service-consulting',
    order: '02',
    lead: '반복 업무를 덜어내는 ',
    highlight: '자동화 컨설팅',
    description: (
      <>
        보고서·정산·고객 응대처럼 매주 반복되는 일을 AI 자동화 흐름으로 바꿉니다. 만들어 드리고 끝이
        아니라 <b className="font-semibold text-foreground">담당자가 직접 운영하도록</b> 정착까지
        함께합니다.
      </>
    ),
    chips: ['업무 진단', '자동화 설계', '보고서 자동화', '문서·메일 요약', '운영 이관'],
    more: { label: '도입 사례 보기', href: '#edu-portfolio' },
    Art: AutomationFlowArt,
  },
];

function ServiceSlide({ service }: { service: Service }): ReactElement {
  const headingId = `${service.id}-heading`;

  return (
    <section id={service.id} aria-labelledby={headingId} className={SLIDE_FRAME}>
      <SlideNumber>{service.order}</SlideNumber>
      <div className="u-container grid items-center gap-12 py-20 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)] lg:gap-20 lg:py-16">
        <div className="flex flex-col items-start gap-7">
          <SlideEyebrow>{`Service ${service.order} · 서비스 안내`}</SlideEyebrow>
          <h2
            id={headingId}
            className="text-[clamp(2rem,1.3rem+2.4vw,3.25rem)] font-bold leading-[1.22] tracking-[-0.05em]"
          >
            {service.lead}
            <Highlight>{service.highlight}</Highlight>
          </h2>
          <p className="max-w-[34rem] text-[16px] leading-[1.85] text-muted-foreground lg:text-[17px]">
            {service.description}
          </p>
          <ul
            aria-label={`${service.highlight} 주요 내용`}
            className="flex max-w-[36rem] flex-wrap gap-2"
          >
            {service.chips.map((chip) => (
              <li
                key={chip}
                className="rounded-full bg-mint-soft/60 px-4 py-2 text-[14px] font-medium text-on-mint"
              >
                {chip}
              </li>
            ))}
          </ul>
          <a
            href={service.more.href}
            className={cn(
              'group mt-3 inline-flex items-center gap-1.5 border-foreground border-b-2 pb-1 text-[16px] font-semibold',
              'focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring',
            )}
          >
            {service.more.label}
            <ArrowDown
              aria-hidden="true"
              className="size-4 transition-transform group-hover:translate-y-0.5"
              strokeWidth={2.2}
            />
          </a>
        </div>
        <service.Art />
      </div>
    </section>
  );
}

/** 서비스 안내 두 장 — 슬라이드마다 따로 멈춘다 */
export function EducationServices(): ReactElement {
  return (
    <>
      {SERVICES.map((service) => (
        <ServiceSlide key={service.id} service={service} />
      ))}
    </>
  );
}
